import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Input, Popconfirm, Select, Space, Switch, Typography } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { loadCmsPageOptions } from "../../../components/cms-common/LinkPicker";

// Shared building blocks for the Header / Footer layout forms (ported from
// tykkit's headerBody/footer, adapted to this builder's controlled data/onChange).
//
// Layout is driven by the space the form actually gets (the builder canvas is
// much narrower than the viewport), so rows use wrapping flex / auto-fill grid
// instead of viewport breakpoints.

const { Text } = Typography;

export const EMPTY_LINK = { type: "internal", url: "" };

export const newId = (prefix) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// Immutable list helpers bound to a commit callback.
export const listOps = (list = [], commit) => ({
  update: (index, patch) =>
    commit(list.map((item, idx) => (idx === index ? { ...item, ...patch } : item))),
  remove: (index) => commit(list.filter((_, idx) => idx !== index)),
  move: (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
  },
  add: (item) => commit([...list, item]),
});

/* ─── Layout primitives ─────────────────────────────────────────────── */

// Grid that fits as many `min`-wide columns as the container allows.
export const FieldGrid = ({ min = 220, gap = 16, children, style }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
      gap,
      ...style,
    }}
  >
    {children}
  </div>
);

// Wrapping row: children declare their own `flex` basis and wrap when tight.
export const WrapRow = ({ gap = 10, align = "flex-end", children, style }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap, alignItems: align, ...style }}>
    {children}
  </div>
);

export const Field = ({ label, flex, children, style }) => (
  <div style={{ flex, minWidth: 0, ...style }}>
    {label && (
      <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13, color: "#4b5563" }}>
        {label}
      </div>
    )}
    {children}
  </div>
);

export const PanelCard = ({ icon, color = "#8b0000", title, extra, children }) => (
  <Card
    size="small"
    title={
      <Space size={8}>
        <i className={`ri-${icon}`} style={{ color, fontSize: 17 }} />
        <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: "normal" }}>{title}</span>
      </Space>
    }
    extra={extra}
    styles={{
      head: { background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
      body: { padding: "clamp(12px, 2.5vw, 20px)" },
    }}
    style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 16 }}
  >
    {children}
  </Card>
);

export const ItemBox = ({ children, style }) => (
  <div
    style={{
      background: "#fcfcfc",
      border: "1px solid #f0f0f0",
      borderRadius: 8,
      padding: 12,
      ...style,
    }}
  >
    {children}
  </div>
);

export const ToggleRow = ({ label, hint, checked, onChange }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
    <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
      <Text strong style={{ fontSize: 13 }}>
        {label}
      </Text>
      {hint && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      )}
    </Space>
    <Switch checked={checked !== false} onChange={onChange} />
  </div>
);

export const RowActions = ({
  index,
  total,
  onMove,
  onRemove,
  confirmText = "Remove this item?",
}) => (
  <Space size={4} style={{ flex: "none" }}>
    <Button
      size="small"
      icon={<ArrowUpOutlined />}
      disabled={index === 0}
      onClick={() => onMove(index, index - 1)}
    />
    <Button
      size="small"
      icon={<ArrowDownOutlined />}
      disabled={index === total - 1}
      onClick={() => onMove(index, index + 1)}
    />
    <Popconfirm title={confirmText} onConfirm={() => onRemove(index)}>
      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
    </Popconfirm>
  </Space>
);

export const AddItemButton = ({ onClick, label, disabled }) => (
  <Button type="dashed" block icon={<PlusOutlined />} onClick={onClick} disabled={disabled}>
    {label}
  </Button>
);

/* ─── LinkField (tykkit UrlInput) ───────────────────────────────────── */

const LINK_TYPES = [
  { label: "Internal", value: "internal" },
  { label: "External", value: "external" },
  { label: "Services", value: "services" },
];

const toLinkObject = (value) => {
  if (value && typeof value === "object")
    return { type: value.type || "internal", url: value.url || "" };
  if (typeof value === "string" && value) {
    return { type: value.startsWith("http") ? "external" : "internal", url: value };
  }
  return { ...EMPTY_LINK };
};

// Type selector + destination. Internal → searchable page list, External → URL input.
export const LinkField = ({ value, onChange }) => {
  const link = toLinkObject(value);
  const latest = useRef(link);
  latest.current = link;
  const [cmsPages, setCmsPages] = useState([]);

  useEffect(() => {
    let active = true;
    loadCmsPageOptions().then((pages) => active && setCmsPages(pages));
    return () => {
      active = false;
    };
  }, []);

  const pageOptions = cmsPages.filter((g) => g.label !== "Services");
  const serviceOptions = cmsPages.find((g) => g.label === "Services")?.options || [];

  const trigger = (changed) => {
    const next = { ...latest.current, ...changed };
    latest.current = next;
    onChange?.(next);
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, width: "100%" }}>
      <Select
        style={{ flex: "0 0 118px" }}
        value={link.type}
        options={LINK_TYPES}
        onChange={(type) => trigger({ type, url: "" })}
      />
      <div style={{ flex: "1 1 170px", minWidth: 0 }}>
        {link.type === "internal" ? (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Select Page"
            value={link.url || undefined}
            options={pageOptions}
            optionFilterProp="label"
            onChange={(url) => trigger({ url: url || "" })}
          />
        ) : link.type === "services" ? (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Select Category"
            value={link.url || undefined}
            options={serviceOptions}
            optionFilterProp="label"
            onChange={(url) => trigger({ url: url || "" })}
          />
        ) : (
          <Input
            placeholder="https://example.com"
            value={link.url}
            onChange={(e) => trigger({ url: e.target.value })}
          />
        )}
      </div>
    </div>
  );
};

/* ─── Lists ─────────────────────────────────────────────────────────── */

// Controlled list of { id, label, link } rows.
export const LinkListEditor = ({
  links = [],
  onChange,
  addLabel = "Add Link",
  labelPlaceholder = "e.g. Navigation Link Name",
}) => {
  const ops = listOps(links, onChange);

  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      {links.map((item, index) => (
        <ItemBox key={item.id || index}>
          <WrapRow>
            <Field label={index === 0 ? "Link Label" : null} flex="1 1 180px">
              <Input
                value={item.label || ""}
                placeholder={labelPlaceholder}
                onChange={(e) => ops.update(index, { label: e.target.value })}
              />
            </Field>
            <Field label={index === 0 ? "Link Destination" : null} flex="2 1 300px">
              <LinkField value={item.link} onChange={(link) => ops.update(index, { link })} />
            </Field>
            <RowActions
              index={index}
              total={links.length}
              onMove={ops.move}
              onRemove={ops.remove}
            />
          </WrapRow>
        </ItemBox>
      ))}
      <AddItemButton
        label={addLabel}
        onClick={() => ops.add({ id: newId("lnk"), label: "", link: { ...EMPTY_LINK } })}
      />
    </Space>
  );
};

// tykkit CMSPlaceholderList: numbered inputs stored as [{ content }].
export const PlaceholderList = ({
  items = [],
  onChange,
  placeholderText = "e.g. Search products...",
}) => {
  const list = items.map((item) => (typeof item === "string" ? { content: item } : item || {}));
  const ops = listOps(list, onChange);

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {list.length > 0 && (
        <FieldGrid min={220} gap={12}>
          {list.map((item, index) => (
            <div key={item.id || index} style={{ display: "flex", gap: 8 }}>
              <Input
                prefix={
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      background: "#f3f4f6",
                      padding: "2px 6px",
                      borderRadius: 4,
                      marginRight: 4,
                      userSelect: "none",
                    }}
                  >
                    #{index + 1}
                  </span>
                }
                value={item.content || ""}
                placeholder={placeholderText}
                onChange={(e) => ops.update(index, { content: e.target.value })}
              />
              <Button danger icon={<DeleteOutlined />} onClick={() => ops.remove(index)} />
            </div>
          ))}
        </FieldGrid>
      )}
      <AddItemButton
        label="Add Placeholder"
        onClick={() => ops.add({ id: newId("ph"), content: "" })}
      />
    </Space>
  );
};
